import React, { useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Fab,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Assignment as AssignmentIcon,
  CalendarMonth as CalendarIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  LocalHospital as VetIcon,
  MedicalServices as MedIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import {
  useVetVisitReports,
  useCreateVetVisitReport,
  useUpdateVetVisitReport,
  useDeleteVetVisitReport,
} from '../hooks/useVetVisitReports';
import { useBirdProcurements } from '../hooks/useBirds';
import { useAuth } from '../contexts/AuthContext';

const CONDITION_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  good: { label: 'Good', color: '#4caf50', bg: 'rgba(76,175,80,0.12)' },
  fair: { label: 'Fair', color: '#ff9800', bg: 'rgba(255,152,0,0.12)' },
  poor: { label: 'Poor', color: '#ff5722', bg: 'rgba(255,87,34,0.12)' },
  critical: { label: 'Critical', color: '#f44336', bg: 'rgba(244,67,54,0.12)' },
};

const EMPTY_FORM = {
  procurement_id: '',
  visit_date: '',
  vet_name: '',
  bird_condition: 'good',
  observed_mortality: '',
  findings: '',
  diagnosis: '',
  recommendations: '',
  medications_prescribed: '',
  next_visit_date: '',
  notes: '',
};

const formatDate = (d: string) => {
  try {
    return format(parseISO(d), 'd MMM yyyy');
  } catch {
    return d;
  }
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      color: 'rgba(255,255,255,0.45)',
      fontWeight: 700,
      letterSpacing: '0.8px',
      fontSize: '0.6rem',
      display: 'block',
      mb: 1,
      mt: 2,
    }}
  >
    {String(children).toUpperCase()}
  </Typography>
);

const VetVisitReports: React.FC = () => {
  const [formOpen, setFormOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<any | null>(null);
  const [editing, setEditing] = useState<any | null>(null);
  const [selected, setSelected] = useState<any | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [batchFilter, setBatchFilter] = useState<string>('');

  const { user } = useAuth();
  const isVet = user?.role === 'veterinarian';

  const params = batchFilter ? { procurement_id: batchFilter } : undefined;
  const { data: reportsData, isFetching } = useVetVisitReports(params);
  const { data: procData } = useBirdProcurements();

  const reports: any[] = reportsData?.data ?? [];

  const procurements = useMemo(() => procData?.data || [], [procData]);

  const createMutation = useCreateVetVisitReport();
  const updateMutation = useUpdateVetVisitReport();
  const deleteMutation = useDeleteVetVisitReport();

  const openAdd = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setFormOpen(true);
  };

  const openEdit = (r: any) => {
    setEditing(r);
    setForm({
      procurement_id: r.procurement_id ?? '',
      visit_date: r.visit_date ?? '',
      vet_name: r.vet_name ?? '',
      bird_condition: r.bird_condition ?? 'good',
      observed_mortality: r.observed_mortality ?? '',
      findings: r.findings ?? '',
      diagnosis: r.diagnosis ?? '',
      recommendations: r.recommendations ?? '',
      medications_prescribed: r.medications_prescribed ?? '',
      next_visit_date: r.next_visit_date ?? '',
      notes: r.notes ?? '',
    });
    setFormOpen(true);
  };

  const handleSave = () => {
    const payload = {
      ...form,
      observed_mortality: form.observed_mortality === '' ? 0 : Number(form.observed_mortality),
    };

    if (editing) {
      updateMutation.mutate(
        { id: editing.id, data: payload },
        { onSuccess: () => setFormOpen(false) }
      );
    } else {
      createMutation.mutate(payload, { onSuccess: () => setFormOpen(false) });
    }
  };

  const handleDelete = () => {
    if (!deleteConfirm) return;
    deleteMutation.mutate(deleteConfirm.id, { onSuccess: () => setDeleteConfirm(null) });
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const totalReports = reports.length;
  const nextVisitCount = reports.filter(
    (r) => r.next_visit_date && (r.days_until_next_visit ?? 0) >= 0
  ).length;
  const criticalCount = reports.filter(
    (r) => r.bird_condition === 'critical' || r.bird_condition === 'poor'
  ).length;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {isFetching && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 2,
            bgcolor: 'transparent',
            '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #ff9800, #ffcc02)' },
          }}
        />
      )}

      {/* Header */}
      <Box
        sx={{
          mb: 3,
          p: 3,
          borderRadius: 2.5,
          background: 'linear-gradient(135deg, #141414 0%, #1c1c1c 100%)',
          border: '1px solid rgba(255,152,0,0.18)',
          display: 'flex',
          alignItems: 'center',
          gap: 2.5,
          position: 'relative',
          overflow: 'hidden',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            width: '35%',
            background:
              'radial-gradient(ellipse at right center, rgba(255,152,0,0.06) 0%, transparent 70%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Avatar
          sx={{
            bgcolor: 'rgba(255,152,0,0.12)',
            color: '#ff9800',
            width: 56,
            height: 56,
            border: '1px solid rgba(255,152,0,0.25)',
          }}
        >
          <VetIcon />
        </Avatar>
        <Box flex={1}>
          <Typography
            variant="h5"
            fontWeight={800}
            sx={{ color: '#fff', letterSpacing: '-0.02em', lineHeight: 1.2 }}
          >
            Vet Visit Reports
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.52)', mt: 0.4 }}>
            Farm visitation records · linked per batch
          </Typography>
        </Box>
        {isVet && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAdd}
            sx={{
              background: 'linear-gradient(135deg, #ff9800, #f57c00)',
              color: '#000',
              fontWeight: 700,
              px: 2.5,
            }}
          >
            New Report
          </Button>
        )}
      </Box>

      {/* Stat cards */}
      <Grid container spacing={2} mb={3}>
        {[
          { label: 'Total Reports', value: totalReports, accent: '#ff9800', Icon: AssignmentIcon },
          {
            label: 'Upcoming Visits',
            value: nextVisitCount,
            accent: '#42a5f5',
            Icon: CalendarIcon,
          },
          { label: 'Attention Needed', value: criticalCount, accent: '#f44336', Icon: MedIcon },
        ].map(({ label, value, accent, Icon }) => (
          <Grid key={label} size={{ xs: 12, sm: 4 }}>
            <Paper
              sx={{
                p: 2.5,
                background: 'linear-gradient(145deg, #181818 0%, #1d1d1d 100%)',
                border: `1px solid ${accent}28`,
                borderRadius: 2.5,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  background: `linear-gradient(90deg, ${accent}, ${accent}44)`,
                  borderRadius: '10px 10px 0 0',
                },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'rgba(255,255,255,0.45)',
                      fontWeight: 700,
                      letterSpacing: '1px',
                      fontSize: '0.62rem',
                      display: 'block',
                      mb: 0.5,
                    }}
                  >
                    {label.toUpperCase()}
                  </Typography>
                  <Typography variant="h4" sx={{ color: accent, fontWeight: 800, lineHeight: 1 }}>
                    {value}
                  </Typography>
                </Box>
                <Avatar
                  sx={{
                    bgcolor: `${accent}14`,
                    color: accent,
                    width: 42,
                    height: 42,
                    border: `1px solid ${accent}22`,
                  }}
                >
                  <Icon fontSize="small" />
                </Avatar>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Batch filter */}
      <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          select
          size="small"
          label="Filter by Batch"
          value={batchFilter}
          onChange={(e) => setBatchFilter(e.target.value)}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">All Batches</MenuItem>
          {procurements.map((p: any) => (
            <MenuItem key={p.id} value={p.id}>
              {p.identifier}
            </MenuItem>
          ))}
        </TextField>
        {batchFilter && (
          <Button
            size="small"
            onClick={() => setBatchFilter('')}
            sx={{ color: 'rgba(255,255,255,0.45)' }}
          >
            Clear
          </Button>
        )}
      </Box>

      {/* Report list */}
      {reports.length === 0 && !isFetching ? (
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            background: '#141414',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: 2.5,
          }}
        >
          <VetIcon sx={{ fontSize: 56, color: 'rgba(255,152,0,0.2)', mb: 1.5 }} />
          <Typography sx={{ color: 'rgba(255,255,255,0.4)' }}>No visit reports yet.</Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.25)' }}>
            Click &quot;New Report&quot; to log the first visitation.
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {reports.map((r: any) => {
            const cond = CONDITION_CONFIG[r.bird_condition] ?? CONDITION_CONFIG.good;
            const daysUntil = r.days_until_next_visit;
            const nextVisitLabel =
              daysUntil === null
                ? null
                : daysUntil < 0
                  ? `${Math.abs(daysUntil)}d overdue`
                  : daysUntil === 0
                    ? 'Visit today'
                    : `Next visit in ${daysUntil}d`;
            const nextVisitColor =
              daysUntil === null
                ? ''
                : daysUntil <= 0
                  ? '#f44336'
                  : daysUntil <= 3
                    ? '#ff9800'
                    : '#42a5f5';

            return (
              <Paper
                key={r.id}
                sx={{
                  p: 2.5,
                  background: 'linear-gradient(145deg, #161616, #1b1b1b)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  borderLeft: `3px solid ${cond.color}`,
                  borderRadius: 2.5,
                  transition: 'border-color 0.15s',
                  '&:hover': { borderColor: 'rgba(255,255,255,0.12)', borderLeftColor: cond.color },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    gap: 1.5,
                  }}
                >
                  {/* Left: batch + meta */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                        mb: 0.75,
                      }}
                    >
                      <Typography sx={{ fontWeight: 800, color: '#fff', fontSize: '0.95rem' }}>
                        {r.procurement?.identifier ?? `Batch #${r.procurement_id}`}
                      </Typography>
                      <Chip
                        label={cond.label}
                        size="small"
                        sx={{
                          bgcolor: cond.bg,
                          color: cond.color,
                          fontWeight: 700,
                          fontSize: '0.62rem',
                          height: 20,
                          border: `1px solid ${cond.color}33`,
                        }}
                      />
                      {nextVisitLabel && (
                        <Chip
                          label={nextVisitLabel}
                          size="small"
                          sx={{
                            bgcolor: `${nextVisitColor}14`,
                            color: nextVisitColor,
                            fontWeight: 700,
                            fontSize: '0.62rem',
                            height: 20,
                            border: `1px solid ${nextVisitColor}33`,
                          }}
                        />
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)' }}>
                        <strong style={{ color: 'rgba(255,255,255,0.65)' }}>
                          Dr. {r.vet_name}
                        </strong>
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)' }}>
                        {formatDate(r.visit_date)}
                      </Typography>
                      {r.observed_mortality > 0 && (
                        <Typography variant="caption" sx={{ color: '#f44336' }}>
                          {r.observed_mortality} mortalities observed
                        </Typography>
                      )}
                    </Box>

                    {r.diagnosis && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          color: 'rgba(255,255,255,0.45)',
                          mt: 0.75,
                          fontSize: '0.72rem',
                          lineHeight: 1.5,
                        }}
                      >
                        <span
                          style={{
                            color: 'rgba(255,255,255,0.28)',
                            fontWeight: 700,
                            letterSpacing: '0.5px',
                            fontSize: '0.6rem',
                          }}
                        >
                          DIAGNOSIS{' '}
                        </span>
                        {r.diagnosis.length > 120 ? r.diagnosis.slice(0, 120) + '…' : r.diagnosis}
                      </Typography>
                    )}
                  </Box>

                  {/* Actions */}
                  <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                    <Tooltip title="View full report">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelected(r);
                          setViewOpen(true);
                        }}
                        sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#ff9800' } }}
                      >
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {isVet && (
                      <>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => openEdit(r)}
                            sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#42a5f5' } }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => setDeleteConfirm(r)}
                            sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#f44336' } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}

      {/* FAB */}
      {isVet && (
        <Fab
          color="primary"
          onClick={openAdd}
          sx={{
            position: 'fixed',
            bottom: 32,
            right: 32,
            background: 'linear-gradient(135deg, #ff9800, #f57c00)',
            color: '#000',
            display: { sm: 'none' },
          }}
        >
          <AddIcon />
        </Fab>
      )}

      {/* ── Add / Edit dialog ── */}
      <Dialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              background: 'linear-gradient(145deg, #1a1a1a, #222)',
              border: '1px solid rgba(255,152,0,0.2)',
              borderRadius: 2.5,
            },
          },
        }}
      >
        <DialogTitle sx={{ color: '#fff', fontWeight: 700, pb: 0.5 }}>
          {editing ? 'Edit Visit Report' : 'New Vet Visit Report'}
        </DialogTitle>

        <DialogContent sx={{ pt: '12px !important' }}>
          <SectionLabel>Visit Details</SectionLabel>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                select
                label="Batch"
                fullWidth
                size="small"
                required
                value={form.procurement_id}
                onChange={(e) => setForm((f) => ({ ...f, procurement_id: e.target.value }))}
              >
                {procurements.map((p: any) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.identifier}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Visit Date"
                type="date"
                fullWidth
                size="small"
                required
                InputLabelProps={{ shrink: true }}
                value={form.visit_date}
                onChange={(e) => setForm((f) => ({ ...f, visit_date: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Vet Doctor Name"
                fullWidth
                size="small"
                required
                value={form.vet_name}
                onChange={(e) => setForm((f) => ({ ...f, vet_name: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                label="Bird Condition"
                fullWidth
                size="small"
                value={form.bird_condition}
                onChange={(e) => setForm((f) => ({ ...f, bird_condition: e.target.value }))}
              >
                {Object.entries(CONDITION_CONFIG).map(([k, v]) => (
                  <MenuItem key={k} value={k}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: v.color }} />
                      {v.label}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Observed Mortality"
                type="number"
                fullWidth
                size="small"
                inputProps={{ min: 0 }}
                value={form.observed_mortality}
                onChange={(e) => setForm((f) => ({ ...f, observed_mortality: e.target.value }))}
              />
            </Grid>
          </Grid>

          <SectionLabel>Clinical Notes</SectionLabel>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Findings / Observations"
                multiline
                rows={3}
                fullWidth
                size="small"
                placeholder="General flock condition, symptoms observed, pen inspection notes…"
                value={form.findings}
                onChange={(e) => setForm((f) => ({ ...f, findings: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Diagnosis"
                multiline
                rows={2}
                fullWidth
                size="small"
                value={form.diagnosis}
                onChange={(e) => setForm((f) => ({ ...f, diagnosis: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Recommendations"
                multiline
                rows={2}
                fullWidth
                size="small"
                value={form.recommendations}
                onChange={(e) => setForm((f) => ({ ...f, recommendations: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Medications Prescribed"
                multiline
                rows={2}
                fullWidth
                size="small"
                placeholder="Drug name, dosage, duration…"
                value={form.medications_prescribed}
                onChange={(e) => setForm((f) => ({ ...f, medications_prescribed: e.target.value }))}
              />
            </Grid>
          </Grid>

          <SectionLabel>Follow-up</SectionLabel>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Next Visit Date"
                type="date"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={form.next_visit_date}
                onChange={(e) => setForm((f) => ({ ...f, next_visit_date: e.target.value }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Notes"
                fullWidth
                size="small"
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setFormOpen(false)} sx={{ color: 'rgba(255,255,255,0.45)' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!form.procurement_id || !form.visit_date || !form.vet_name || isSaving}
            onClick={handleSave}
            sx={{
              background: 'linear-gradient(135deg, #ff9800, #f57c00)',
              color: '#000',
              fontWeight: 700,
              px: 2.5,
            }}
          >
            {isSaving ? 'Saving…' : editing ? 'Save Changes' : 'Save Report'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── View dialog ── */}
      <Dialog
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              background: 'linear-gradient(145deg, #1a1a1a, #222)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 2.5,
            },
          },
        }}
      >
        {selected &&
          (() => {
            const cond = CONDITION_CONFIG[selected.bird_condition] ?? CONDITION_CONFIG.good;
            return (
              <>
                <DialogTitle sx={{ pb: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar
                      sx={{
                        bgcolor: 'rgba(255,152,0,0.12)',
                        color: '#ff9800',
                        width: 40,
                        height: 40,
                      }}
                    >
                      <VetIcon fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>
                        {selected.procurement?.identifier ?? `Batch #${selected.procurement_id}`}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)' }}>
                        Dr. {selected.vet_name} · {formatDate(selected.visit_date)}
                      </Typography>
                    </Box>
                  </Box>
                </DialogTitle>
                <DialogContent>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    <Chip
                      label={cond.label}
                      size="small"
                      sx={{
                        bgcolor: cond.bg,
                        color: cond.color,
                        fontWeight: 700,
                        fontSize: '0.65rem',
                        border: `1px solid ${cond.color}33`,
                      }}
                    />
                    {selected.observed_mortality > 0 && (
                      <Chip
                        label={`${selected.observed_mortality} mortalities`}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(244,67,54,0.12)',
                          color: '#f44336',
                          fontWeight: 700,
                          fontSize: '0.65rem',
                        }}
                      />
                    )}
                    {selected.next_visit_date && (
                      <Chip
                        icon={<CalendarIcon sx={{ fontSize: '0.8rem !important' }} />}
                        label={`Next: ${formatDate(selected.next_visit_date)}`}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(66,165,245,0.1)',
                          color: '#42a5f5',
                          fontWeight: 700,
                          fontSize: '0.65rem',
                        }}
                      />
                    )}
                  </Box>

                  {[
                    { label: 'Findings / Observations', value: selected.findings },
                    { label: 'Diagnosis', value: selected.diagnosis },
                    { label: 'Recommendations', value: selected.recommendations },
                    { label: 'Medications Prescribed', value: selected.medications_prescribed },
                    { label: 'Notes', value: selected.notes },
                  ]
                    .filter(({ value }) => value)
                    .map(({ label, value }) => (
                      <Box key={label} sx={{ mb: 2 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'rgba(255,255,255,0.35)',
                            fontWeight: 700,
                            letterSpacing: '0.6px',
                            fontSize: '0.6rem',
                            display: 'block',
                            mb: 0.5,
                          }}
                        >
                          {label.toUpperCase()}
                        </Typography>
                        <Typography
                          sx={{
                            color: 'rgba(255,255,255,0.8)',
                            fontSize: '0.875rem',
                            lineHeight: 1.6,
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          {value}
                        </Typography>
                        <Divider sx={{ mt: 1.5, borderColor: 'rgba(255,255,255,0.05)' }} />
                      </Box>
                    ))}

                  {selected.recorded_by && (
                    <Typography
                      variant="caption"
                      sx={{ color: 'rgba(255,255,255,0.25)', display: 'block', mt: 1 }}
                    >
                      Recorded by {selected.recorded_by.name}
                    </Typography>
                  )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                  <Button
                    onClick={() => setViewOpen(false)}
                    sx={{ color: 'rgba(255,255,255,0.45)' }}
                  >
                    Close
                  </Button>
                  {isVet && (
                    <Button
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => {
                        setViewOpen(false);
                        openEdit(selected);
                      }}
                      sx={{
                        color: '#42a5f5',
                        borderColor: 'rgba(66,165,245,0.35)',
                        fontWeight: 700,
                      }}
                    >
                      Edit
                    </Button>
                  )}
                </DialogActions>
              </>
            );
          })()}
      </Dialog>

      {/* ── Delete confirm ── */}
      <Dialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              background: 'linear-gradient(145deg, #1a1a1a, #222)',
              border: '1px solid rgba(244,67,54,0.2)',
              borderRadius: 2.5,
            },
          },
        }}
      >
        <DialogTitle sx={{ color: '#fff', fontWeight: 700 }}>Delete Report</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>
            Delete the visit report for{' '}
            <strong style={{ color: '#ffa726' }}>
              {deleteConfirm?.procurement?.identifier ?? `Batch #${deleteConfirm?.procurement_id}`}
            </strong>{' '}
            on{' '}
            <strong style={{ color: '#ffa726' }}>
              {deleteConfirm ? formatDate(deleteConfirm.visit_date) : ''}
            </strong>
            ?
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.25)', display: 'block', mt: 1 }}
          >
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setDeleteConfirm(null)} sx={{ color: 'rgba(255,255,255,0.45)' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={deleteMutation.isPending}
            onClick={handleDelete}
            sx={{
              background: 'linear-gradient(135deg, #f44336, #c62828)',
              color: '#fff',
              fontWeight: 700,
            }}
          >
            {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VetVisitReports;
